/**
 * Sovereign Powers.
 *
 * Cast with a hotkey, paid for in Will, aimed with a ground reticle, and loud
 * enough that the player looks up from the economy to watch. Three upgrade
 * tiers each, bought with Souls.
 *
 * Design rule: a power should change the shape of a fight, not shave a
 * percentage off it. If the answer to "what did that do" is a number, it is the
 * wrong power.
 *
 * ── The direct-damage family is gone (2026-08-19) ──────────────────────────
 *
 * The owner, verbatim: *"remove the starrfall arrow storm abilties like all of
 * them they are insanly op"*. Five powers came out of the roster and are listed
 * in `RETIRED_POWERS` below: **Starfall, Arrow Storm, Ember Rain, Chain
 * Lightning and Rot Wind**. Every one of them had the same shape — point at a
 * crowd, pay Will, the crowd is dead — and that shape is the complaint. Starfall
 * alone put 180 blunt into a 3.4-tile circle every 26 seconds with a 40% damage
 * tier for 60 Souls, on a settlement whose whole defence is meant to be walls,
 * towers and men.
 *
 * The rule the cut was made by, so the next power can be judged against it:
 * **a power comes out if its primary output is damage to enemy units.** Not
 * "if it does any damage" — Tremor's 60 blunt and Gale's 20 are the receipt for
 * a knockdown and a shove, and Shatterstone's 400 is spent on siege engines and
 * stonework, which is a specific answer to a specific problem rather than an
 * answer to the wave. What survives is fifteen powers of utility, control,
 * support and ground-shaping: they make a defence work, and a player who has not
 * built one still loses.
 *
 * The Sovereign is not left without a verb — that is its own kind of broken —
 * so `haul_down` was written to stand where Starfall stood, in the Hearth's own
 * grant and in the opening kit. It is deliberately the *inverse* of a nuke: it
 * moves the enemy and kills nothing, so it is worth exactly as much as whatever
 * you have waiting where you drag them.
 *
 * Nothing here breaks a save. `powerOrNull` simply stops resolving the five ids,
 * `castPower` refuses them, and `loadWorld` strips them out of `unlockedPowers`
 * and `powerTiers` on the way in.
 */

export type Targeting = 'ground' | 'self' | 'line' | 'company';

export interface PowerTier {
  /** Souls to unlock this tier. */
  soulCost: number;
  /** What the upgrade actually does, in the player's words. */
  text: string;
  /** Multipliers applied at cast time. */
  damageMul?: number;
  radiusMul?: number;
  durationMul?: number;
  cooldownMul?: number;
  costMul?: number;
  extra?: string;
}

export interface PowerDef {
  id: string;
  name: string;
  flavour: string;
  /** Short mechanical line for the tooltip. Exact numbers, no vagueness. */
  rules: string;
  targeting: Targeting;
  /** Will cost. */
  cost: number;
  /** Seconds. */
  cooldown: number;
  radius: number;
  /** Seconds of telegraph before it lands. 0 = instant. */
  delay: number;
  damage: number;
  duration: number;
  /** Does it hit your own people too? The big ones should. */
  friendlyFire: boolean;
  /** Single glyph the UI draws for the wax-seal button. */
  glyph: string;
  /** Age at which it becomes available in the unlock pool. */
  age: number;
  tiers: [PowerTier, PowerTier, PowerTier];
}

function tiers(a: PowerTier, b: PowerTier, c: PowerTier): [PowerTier, PowerTier, PowerTier] {
  return [a, b, c];
}

export const POWERS: PowerDef[] = [
  {
    id: 'haul_down',
    name: 'Haul Them Down',
    flavour: 'Grapnels, chains and eleven people pulling. It is not sorcery and it has never needed to be.',
    rules: 'Drags every enemy within 5 tiles toward the mark and leaves them off their feet for 1.1s. No damage at all.',
    targeting: 'ground',
    cost: 26,
    cooldown: 22,
    radius: 5,
    delay: 0,
    damage: 0,
    duration: 1.1,
    friendlyFire: false,
    glyph: '⤓',
    age: 1,
    tiers: tiers(
      { soulCost: 50, text: 'Radius 5 → 6.8 tiles.', radiusMul: 1.36 },
      { soulCost: 140, text: 'They stay down 1.1 → 1.9s.', durationMul: 1.72 },
      { soulCost: 300, text: 'Anything dragged loses its armour for 6s.', extra: 'sunder' },
    ),
  },
  {
    id: 'rally_banner',
    name: 'Rally the Banner',
    flavour: 'The banner is a rag on a stick. It has never once failed to work.',
    rules: '+25% attack speed, +15% damage and full morale for 10s in a 6 tile circle.',
    targeting: 'ground',
    cost: 30,
    cooldown: 22,
    radius: 6,
    delay: 0,
    damage: 0,
    duration: 10,
    friendlyFire: false,
    glyph: '⚑',
    age: 1,
    tiers: tiers(
      { soulCost: 50, text: 'Duration 10 → 15s.', durationMul: 1.5 },
      { soulCost: 140, text: 'Radius 6 → 8.5 tiles.', radiusMul: 1.4 },
      { soulCost: 300, text: 'Also heals 25% of missing health on cast.', extra: 'heal' },
    ),
  },
  {
    id: 'wall_of_bramble',
    name: 'Wall of Bramble',
    flavour: 'It grows where it is told. It does not stop where it is told.',
    rules: 'Grows 7 bramble segments across a line for 20s. They block movement and thorn anything that hits them.',
    targeting: 'line',
    cost: 40,
    cooldown: 30,
    radius: 5,
    delay: 0,
    damage: 0,
    duration: 20,
    friendlyFire: false,
    glyph: '⩚',
    age: 2,
    tiers: tiers(
      { soulCost: 70, text: 'Segments have +80% health.', damageMul: 1.8 },
      { soulCost: 170, text: 'Line length 5 → 8 tiles.', radiusMul: 1.6 },
      { soulCost: 340, text: 'Duration 20 → 35s.', durationMul: 1.75 },
    ),
  },
  {
    id: 'sanctified_ground',
    name: 'Sanctified Ground',
    flavour: 'Consecrated in a hurry, by a man who was not entirely sure of the words.',
    rules: 'Heals 5% max health per second and grants 20% damage reduction in a 4.5 tile circle for 12s.',
    targeting: 'ground',
    cost: 35,
    cooldown: 28,
    radius: 4.5,
    delay: 0,
    damage: 0,
    duration: 12,
    friendlyFire: false,
    glyph: '✚',
    age: 1,
    tiers: tiers(
      { soulCost: 60, text: 'Healing 5% → 8% per second.', damageMul: 1.6 },
      { soulCost: 150, text: 'Duration 12 → 18s.', durationMul: 1.5 },
      { soulCost: 320, text: 'Also cleanses poison, fear and madness.', extra: 'cleanse' },
    ),
  },
  {
    id: 'grave_call',
    name: 'Grave-Call',
    flavour: 'They fell for you once. It is not, strictly, a great deal to ask.',
    rules: 'Raises up to 10 of your own recent dead as Called Dead for 30s. They do not count against population.',
    targeting: 'ground',
    cost: 40,
    cooldown: 34,
    radius: 6,
    delay: 0,
    damage: 0,
    duration: 30,
    friendlyFire: false,
    glyph: '⚱',
    age: 2,
    tiers: tiers(
      { soulCost: 70, text: 'Raises up to 16.', extra: 'more' },
      { soulCost: 170, text: 'Called Dead last 30 → 50s.', durationMul: 1.66 },
      { soulCost: 350, text: 'They rise with 60% more health.', damageMul: 1.6 },
    ),
  },
  {
    id: 'tremor',
    name: 'Tremor',
    flavour: 'The ground remembers being a mountain, and has a bad week about it.',
    rules: 'A 9 tile line: 60 blunt damage, knocks down for 1.6s. Large enemies are staggered but not floored.',
    targeting: 'line',
    cost: 32,
    cooldown: 22,
    radius: 9,
    delay: 0.4,
    damage: 60,
    duration: 1.6,
    friendlyFire: false,
    glyph: '≈',
    age: 2,
    tiers: tiers(
      { soulCost: 60, text: '+50% damage.', damageMul: 1.5 },
      { soulCost: 150, text: 'Knockdown 1.6 → 2.6s.', durationMul: 1.62 },
      { soulCost: 320, text: 'Also cracks walls and siege engines for 200.', extra: 'siege' },
    ),
  },
  {
    id: 'toll_the_bell',
    name: 'Toll the Bell',
    flavour: 'Nine bells were cast. This is the one they buried.',
    rules: 'Enemies in a 7 tile circle rout for 6s. Undead, siege and champions are unmoved.',
    targeting: 'ground',
    cost: 45,
    cooldown: 40,
    radius: 7,
    delay: 0.5,
    damage: 0,
    duration: 6,
    friendlyFire: false,
    glyph: '🕭',
    age: 3,
    tiers: tiers(
      { soulCost: 80, text: 'Radius 7 → 9.5 tiles.', radiusMul: 1.35 },
      { soulCost: 190, text: 'Rout 6 → 10s.', durationMul: 1.66 },
      { soulCost: 400, text: 'Now works on the undead.', extra: 'undead' },
    ),
  },
  {
    id: 'time_in_amber',
    name: 'Time-in-Amber',
    flavour: 'A small, local disagreement with the hour.',
    rules: 'Enemies in a 5 tile circle move and attack 60% slower for 8s.',
    targeting: 'ground',
    cost: 38,
    cooldown: 30,
    radius: 5,
    delay: 0,
    damage: 0,
    duration: 8,
    friendlyFire: false,
    glyph: '⧗',
    age: 3,
    tiers: tiers(
      { soulCost: 70, text: 'Duration 8 → 13s.', durationMul: 1.62 },
      { soulCost: 180, text: 'Radius 5 → 7 tiles.', radiusMul: 1.4 },
      { soulCost: 380, text: 'Also stops projectiles crossing the field.', extra: 'stopshot' },
    ),
  },
  {
    id: 'iron_resolve',
    name: 'Iron Resolve',
    flavour: 'An order given in a voice that does not admit the possibility of retreat.',
    rules: 'Friendly units in 6 tiles take 55% less damage for 7s and cannot rout.',
    targeting: 'ground',
    cost: 40,
    cooldown: 36,
    radius: 6,
    delay: 0,
    damage: 0,
    duration: 7,
    friendlyFire: false,
    glyph: '⛨',
    age: 2,
    tiers: tiers(
      { soulCost: 70, text: 'Duration 7 → 11s.', durationMul: 1.57 },
      { soulCost: 170, text: 'Radius 6 → 8.5 tiles.', radiusMul: 1.42 },
      { soulCost: 360, text: 'Reflects 20% of blocked damage.', extra: 'reflect' },
    ),
  },
  {
    id: 'blood_price',
    name: 'The Blood Price',
    flavour: 'The ledger is honest, at least. That is more than can be said for the Crown.',
    rules: 'Costs 12% of the health of every friendly unit in 6 tiles. They deal +70% damage for 9s.',
    targeting: 'ground',
    cost: 25,
    cooldown: 40,
    radius: 6,
    delay: 0,
    damage: 0,
    duration: 9,
    friendlyFire: false,
    glyph: '⚔',
    age: 3,
    tiers: tiers(
      { soulCost: 80, text: 'Health cost 12% → 7%.', costMul: 0.6 },
      { soulCost: 200, text: 'Damage +70% → +110%.', damageMul: 1.35 },
      { soulCost: 420, text: 'Kills during it heal the killer.', extra: 'lifesteal' },
    ),
  },
  {
    id: 'hearth_light',
    name: 'Hearthlight',
    flavour: 'It is only light. It is only ever only light.',
    rules: 'Reveals a 12 tile circle for 20s and grants +0.15 settlement morale.',
    targeting: 'ground',
    cost: 15,
    cooldown: 18,
    radius: 12,
    delay: 0,
    damage: 0,
    duration: 20,
    friendlyFire: false,
    glyph: '☀',
    age: 1,
    tiers: tiers(
      { soulCost: 40, text: 'Radius 12 → 17 tiles.', radiusMul: 1.4 },
      { soulCost: 110, text: 'Enemies inside take +15% damage.', extra: 'mark' },
      { soulCost: 260, text: 'Also regenerates 8 Will over the duration.', extra: 'will' },
    ),
  },
  {
    id: 'shatterstone',
    name: 'Shatterstone',
    flavour: 'A word for taking things apart, said to the parts.',
    rules: '400 siege damage to structures and siege engines in a 4 tile circle. Barely notices infantry.',
    targeting: 'ground',
    cost: 35,
    cooldown: 26,
    radius: 4,
    delay: 0.8,
    damage: 400,
    duration: 0,
    friendlyFire: false,
    glyph: '⛏',
    age: 2,
    tiers: tiers(
      { soulCost: 60, text: '+50% damage.', damageMul: 1.5 },
      { soulCost: 160, text: 'Radius 4 → 5.5 tiles.', radiusMul: 1.38 },
      { soulCost: 340, text: 'Also repairs your walls in range by 200.', extra: 'repair' },
    ),
  },
  {
    id: 'pale_summons',
    name: 'Pale Summons',
    flavour: 'Twelve of them. Always twelve. Nobody has asked why.',
    rules: 'Calls 12 Called Dead at the reticle for 25s.',
    targeting: 'ground',
    cost: 45,
    cooldown: 38,
    radius: 2.5,
    delay: 0.5,
    damage: 0,
    duration: 25,
    friendlyFire: false,
    glyph: '☩',
    age: 3,
    tiers: tiers(
      { soulCost: 80, text: '12 → 18 of them.', extra: 'more' },
      { soulCost: 190, text: 'They last 25 → 40s.', durationMul: 1.6 },
      { soulCost: 400, text: 'They rise armoured.', damageMul: 1.7 },
    ),
  },
  {
    id: 'gale',
    name: 'Gale',
    flavour: 'The wind off the Ashen Steps, asked for a favour.',
    rules: 'A 10 tile cone: hurls enemies back 4 tiles and staggers them for 1s.',
    targeting: 'line',
    cost: 28,
    cooldown: 20,
    radius: 10,
    delay: 0,
    damage: 20,
    duration: 1,
    friendlyFire: false,
    glyph: '≫',
    age: 2,
    tiers: tiers(
      { soulCost: 55, text: 'Knockback 4 → 6.5 tiles.', radiusMul: 1.5 },
      { soulCost: 145, text: 'Also scatters enemy volleys in the cone.', extra: 'stopshot' },
      { soulCost: 310, text: 'Enemies thrown into walls take 90 damage.', extra: 'slam' },
    ),
  },
  {
    id: 'quicklime',
    name: 'Quicklime',
    flavour: 'Used, once, on a field. The field has not recovered and neither has the ledger.',
    rules: 'A 4.5 tile patch for 12s. Enemies inside lose all armour benefit.',
    targeting: 'ground',
    cost: 30,
    cooldown: 26,
    radius: 4.5,
    delay: 0,
    damage: 0,
    duration: 12,
    friendlyFire: false,
    glyph: '◍',
    age: 3,
    tiers: tiers(
      { soulCost: 60, text: 'Duration 12 → 18s.', durationMul: 1.5 },
      { soulCost: 160, text: 'Radius 4.5 → 6 tiles.', radiusMul: 1.33 },
      { soulCost: 340, text: 'Also deals 8 damage a second.', extra: 'burn' },
    ),
  },
  {
    id: 'last_stand',
    name: 'Last Stand',
    flavour: 'The Sovereign is not supposed to have this. The Sovereign has it.',
    rules: 'Friendly units in 5 tiles cannot drop below 1 health for 5s, then take 25% of what they dodged.',
    targeting: 'ground',
    cost: 55,
    cooldown: 60,
    radius: 5,
    delay: 0,
    damage: 0,
    duration: 5,
    friendlyFire: false,
    glyph: '✜',
    age: 4,
    tiers: tiers(
      { soulCost: 100, text: 'Duration 5 → 7.5s.', durationMul: 1.5 },
      { soulCost: 240, text: 'The debt drops to 10%.', damageMul: 0.4 },
      { soulCost: 500, text: 'Survivors gain +40% damage for 8s after.', extra: 'fury' },
    ),
  },
];

const BY_ID = new Map<string, PowerDef>();
for (const p of POWERS) BY_ID.set(p.id, p);

export function power(id: string): PowerDef {
  const p = BY_ID.get(id);
  if (!p) throw new Error(`Unknown power: ${id}`);
  return p;
}

export function powerOrNull(id: string): PowerDef | undefined {
  return BY_ID.get(id);
}

/**
 * Powers that once existed and no longer do.
 *
 * Kept as a list rather than deleted silently, because three different things
 * need to know that an id is *retired* as against never having existed:
 * `loadWorld` strips them out of a v6 save's `unlockedPowers` and `powerTiers`,
 * `tests/powers.test.ts` asserts none of them can be cast or resolved, and the
 * next person to wonder why the roster has a hole in it gets an answer instead
 * of a git log. Never reuse one of these ids for something new — a save from
 * before the cut would arrive holding a tier for it.
 */
export const RETIRED_POWERS: readonly string[] = [
  'starfall',
  'arrow_storm',
  'ember_rain',
  'chain_lightning',
  'rot_wind',
];

export function isRetiredPower(id: string): boolean {
  return RETIRED_POWERS.includes(id);
}

/**
 * The kit the Sovereign starts every run with.
 *
 * Four unique powers at wave zero, exactly as before — Starfall and Arrow Storm
 * came out and Hearthlight and the Hearth's own `haul_down` came in, so the
 * opening hand is the same size and has stopped containing an answer to the
 * first six waves.
 */
export const STARTING_POWERS = ['rally_banner', 'sanctified_ground', 'hearth_light'];
